const ULTRA_HANDLES = new Set(['membership', 'membership-program-annual', 'ultra-trade-membership']);
const CART_CREATE = "mutation FsBuyNow($input: CartInput!) {\n  cartCreate(input: $input) {\n    cart {\n      checkoutUrl\n      totalQuantity\n      lines(first: 50) {\n        nodes {\n          quantity\n          attributes { key value }\n          merchandise { ... on ProductVariant { id } }\n          sellingPlanAllocation { sellingPlan { id } }\n        }\n        pageInfo { hasNextPage }\n      }\n    }\n    userErrors { code field message }\n    warnings { code message }\n  }\n}";

export function attributesFromEntries(entries) {
  return [...entries].filter(([key, value]) => /^properties\[[^\]]+\]$/.test(key) && value !== '')
    .map(([key, value]) => {
      if (typeof value !== 'string') {
        if (value?.size) throw new Error('upload');
        return null;
      }
      return {key:key.slice(11,-1),value};
    }).filter(Boolean);
}
export function checkoutLines(selection, cart) {
  if (!/^\d+$/.test(String(selection.variantId)) || !Number.isSafeInteger(selection.quantity) || selection.quantity < 1 || selection.quantity > 2147483647) throw new Error('selection');
  const selected = {merchandiseId:'gid://shopify/ProductVariant/'+selection.variantId,quantity:selection.quantity,attributes:selection.attributes || []};
  if (selection.sellingPlanId) selected.sellingPlanId = 'gid://shopify/SellingPlan/'+selection.sellingPlanId;
  const members = (cart.items || []).filter(item => ULTRA_HANDLES.has(item.handle) && item.quantity > 0).map(item => {
    const line = {merchandiseId:'gid://shopify/ProductVariant/'+item.variant_id,quantity:item.quantity,attributes:Object.entries(item.properties || {}).filter(([,value])=>value != null && value !== '').map(([key,value])=>({key,value:String(value)}))};
    const plan = item.selling_plan_allocation?.selling_plan?.id;
    if (plan) line.sellingPlanId='gid://shopify/SellingPlan/'+plan;
    return line;
  });
  return [selected,...members];
}
function canonicalLines(lines) {
  const quantities = new Map();
  for (const line of lines) {
    const attrs = [...(line.attributes || [])].filter(a=>a.value != null && a.value !== '').map(a=>[a.key,String(a.value)]).sort((a,b)=>a[0].localeCompare(b[0]));
    const key=JSON.stringify([line.merchandiseId,line.sellingPlanId || '',attrs]);
    quantities.set(key,(quantities.get(key)||0)+line.quantity);
  }
  return JSON.stringify([...quantities].sort((a,b)=>a[0].localeCompare(b[0])));
}
function checkoutError(message, reference) {
  const error=new Error(message);
  error.reference=/^[A-Z0-9_-]{1,90}$/.test(reference || '')?reference:'UNKNOWN';
  return error;
}
function apiCode(code) { return /^[A-Z][A-Z0-9_]{0,60}$/.test(code || '')?code:'REJECTED'; }
export function verifiedCheckout(payload, expected, hostname, shopDomain) {
  const result=payload?.data?.cartCreate;
  if (result?.warnings?.some(warning=>['MERCHANDISE_NOT_ENOUGH_STOCK','MERCHANDISE_OUT_OF_STOCK'].includes(warning.code))) throw new Error('stock');
  if (payload?.errors?.length) throw checkoutError('checkout','API-'+apiCode(payload.errors[0]?.extensions?.code));
  if (result?.userErrors?.length) throw checkoutError('checkout','CART-'+apiCode(result.userErrors[0]?.code));
  if (result?.warnings?.length) throw checkoutError('checkout','WARNING-'+apiCode(result.warnings[0]?.code));
  if (!result?.cart?.checkoutUrl) throw checkoutError('checkout','NO_URL');
  if (!Array.isArray(result.cart.lines?.nodes) || result.cart.lines.pageInfo?.hasNextPage) throw checkoutError('checkout','INCOMPLETE_LINES');
  const actual=result.cart.lines.nodes.map(line=>({merchandiseId:line.merchandise.id,quantity:line.quantity,attributes:line.attributes,sellingPlanId:line.sellingPlanAllocation?.sellingPlan?.id}));
  if (canonicalLines(actual)!==canonicalLines(expected)) throw checkoutError('checkout','LINE_MISMATCH');
  let url;
  try { url=new URL(result.cart.checkoutUrl); } catch { throw checkoutError('checkout','INVALID_URL'); }
  if (url.protocol!=='https:' || ![hostname,shopDomain].includes(url.hostname)) throw checkoutError('checkout','URL_HOST');
  return url.href;
}
// A retry creates another independent checkout, never changes the shopper's cart.
export async function requestJson(url, options={}, transport=fetch, timeoutMs=8000) {
  const {signal,...requestOptions}=options;
  for (let attempt=0;attempt<2;attempt++) {
    if (signal?.aborted) throw checkoutError('connection','CANCELLED');
    const controller=new AbortController();
    const abort=()=>controller.abort();
    signal?.addEventListener('abort',abort,{once:true});
    const timer=setTimeout(abort,timeoutMs);
    try {
      const response=await transport(url,{...requestOptions,signal:controller.signal});
      if (!response.ok) {
        if ([408,429,500,502,503,504].includes(response.status)) throw checkoutError('connection','HTTP'+response.status);
        throw checkoutError('checkout','HTTP'+response.status);
      }
      let payload;
      try { payload=await response.json(); } catch (reason) {
        if (reason instanceof SyntaxError) throw checkoutError('checkout','NON_JSON');
        throw reason;
      }
      // Shopify also returns temporary throttle/server failures with HTTP 200.
      // Never retry a partial result or a permanent/security rejection.
      const errors=payload?.errors;
      if (!payload?.data?.cartCreate?.cart && Array.isArray(errors) && errors.length && errors.every(error=>['THROTTLED','INTERNAL_SERVER_ERROR'].includes(error.extensions?.code))) {
        throw checkoutError('connection','API-'+errors[0].extensions.code);
      }
      return payload;
    } catch (reason) {
      const transient=reason.name==='AbortError' || reason instanceof TypeError || reason.message==='connection';
      if (!transient) throw reason;
      if (attempt || signal?.aborted) throw checkoutError('connection',reason.reference || (reason.name==='AbortError'?'TIMEOUT':'NETWORK'));
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort',abort);
    }
    await new Promise(resolve=>setTimeout(resolve,1000));
  }
}
function setupBuyNow() {
  if (window.fsBuyNowBound) return;
  window.fsBuyNowBound=true;
  let pending=false;
  const observed=new WeakSet();
  const syncers=new Set();
  const bind=()=>{
    document.querySelectorAll('[data-fs-buy-now]').forEach(button=>{
      if(observed.has(button))return;
      const form=button.closest('form'),add=form?.querySelector('[data-add-to-cart]');
      if(!form || !add)return;
      observed.add(button);
      const sync=()=>{
        if (!button.isConnected) { syncers.delete(sync); return; }
        const unavailable=add.disabled || add.classList.contains('btn--loading');
        button.disabled=pending || unavailable;
        const hidden=add.hidden || getComputedStyle(add).display==='none';
        button.hidden=hidden;
        button.closest('.fs-product-purchase-row')?.classList.toggle('fs-product-purchase-row--direct',!hidden);
      };
      syncers.add(sync);
      new MutationObserver(sync).observe(add,{attributes:true,attributeFilter:['disabled','class','hidden','style']});
      sync();
      const clearError=()=>{
        if (pending) return;
        const error=form.querySelector('[data-fs-buy-now-error]');
        if(error){error.hidden=true;error.textContent='';delete error.dataset.reason;}
      };
      form.addEventListener('input',clearError);
      form.addEventListener('change',clearError);
    });
  };
  const reset=()=>{
    pending=false;
    document.querySelectorAll('[data-fs-buy-now]').forEach(button=>{
      button.removeAttribute('aria-busy');
      const label=button.querySelector('[data-fs-buy-now-label]');
      if(label)label.textContent=button.dataset.label;
    });
    syncers.forEach(sync=>sync());
  };
  document.addEventListener('click',async event=>{
    const button=event.target.closest('[data-fs-buy-now]');
    if(!button)return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if(button.disabled || pending)return;
    const form=button.closest('form'),error=form.querySelector('[data-fs-buy-now-error]');
    error.hidden=true;
    const purchaseFields=[...form.elements].filter(field=>field.name==='quantity' || field.name==='id' || /^properties\[/.test(field.name || ''));
    const invalidField=purchaseFields.find(field=>field.willValidate && !field.checkValidity());
    if(invalidField){invalidField.reportValidity();return;}
    pending=true;
    syncers.forEach(sync=>sync());
    button.setAttribute('aria-busy','true');
    button.querySelector('[data-fs-buy-now-label]').textContent=button.dataset.loading;
    const controller=new AbortController();
    const timer=setTimeout(()=>controller.abort(),25000);
    let redirecting=false;
    let stage='SELECTION';
    try{
      if(document.querySelector('[data-fs-ultra-form] button[type="submit"]:disabled:not([hidden])'))throw new Error('membershipPending');
      const data=new FormData(form);
      const variant=form.elements.namedItem('id')?.value || data.get('id');
      const quantity=Number(form.elements.namedItem('quantity')?.value || data.get('quantity') || 1);
      const plan=form.elements.namedItem('selling_plan')?.value || data.get('selling_plan');
      const attributes=attributesFromEntries(data.entries());
      const selection={variantId:variant,quantity,attributes,sellingPlanId:plan};
      checkoutLines(selection,{items:[]});
      stage='CART';
      const existing=await requestJson(button.dataset.cartUrl+'.js',{credentials:'same-origin',headers:{Accept:'application/json'},cache:'no-store',signal:controller.signal});
      if (!Array.isArray(existing?.items)) throw checkoutError('checkout','INVALID_CART');
      const lines=checkoutLines(selection,existing);
      const input={lines,buyerIdentity:{countryCode:button.dataset.country}};
      stage='CREATE';
      const response=await requestJson('/api/2026-07/graphql.json',{method:'POST',credentials:'omit',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({query:CART_CREATE,variables:{input}}),signal:controller.signal});
      stage='VERIFY';
      const url=verifiedCheckout(response,lines,location.hostname,button.dataset.shopDomain);
      stage='REDIRECT';
      location.assign(url);
      redirecting=true;
    }catch(reason){
      const messages={selection:button.dataset.invalid,upload:button.dataset.fileError,membershipPending:button.dataset.memberPending,stock:button.dataset.stockError,connection:button.dataset.connectionError};
      error.textContent=messages[reason.message] || button.dataset.error;
      error.dataset.reason=Object.prototype.hasOwnProperty.call(messages,reason.message)?reason.message:'checkout';
      if (reason.reference || error.dataset.reason==='checkout') {
        const reference=document.createElement('small');
        const code=/^[A-Z0-9_-]{1,90}$/.test(reason.reference || '')?reason.reference:'UNKNOWN';
        reference.textContent='Reference: BN2-'+stage+'-'+code;
        error.append(reference);
      }
      error.hidden=false;
    }finally{
      clearTimeout(timer);
      if(!redirecting)reset();
    }
  },true);
  window.addEventListener('pageshow',reset);
  document.addEventListener('shopify:section:load',bind);
  new MutationObserver(records=>{
    if(records.some(record=>[...record.addedNodes].some(node=>node.nodeType===1 && (node.matches('[data-fs-buy-now]') || node.querySelector('[data-fs-buy-now]')))))bind();
  }).observe(document.body,{childList:true,subtree:true});
  bind();
}
if(typeof document!=='undefined')setupBuyNow();
